import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import DescriptionIcon from "@mui/icons-material/Description";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";

/**
 * Listado GLOBAL de Partes Manuales (solo lectura)
 * - Lista (GET /partes-trabajo/manual)
 * - (Opcional) Usuarios para mostrar nombre de creador (GET /usuarios)
 * - Abrir editor (navigate) -> pasa readOnly si no es creador
 * - Enviar email (POST /partes-trabajo/manual/:id/enviar-email) -> solo creador (UI)
 *
 * ✅ No permite crear. La creación debe hacerse desde "Mis partes".
 */
export const ListadoPartesManuales = () => {
  const { auth } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // --------------------------
  // State
  // --------------------------
  const [partes, setPartes] = useState([]);
  const [usuarios, setUsuarios] = useState([]); // ✅ para mostrar "Creador"
  const [cargando, setCargando] = useState(true);

  // filtros (sin estado)
  const [filtroAnio, setFiltroAnio] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [busquedaTexto, setBusquedaTexto] = useState("");

  // DataGrid paginación
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Orden controlada
  const [orden, setOrden] = useState({ campo: "creado_en", ascendente: false });

  // --------------------------
  // Email modal
  // --------------------------
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedParte, setSelectedParte] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);

  // --------------------------
  // Helpers
  // --------------------------
  const token = useMemo(() => localStorage.getItem("token"), []);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const isEmailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

  const normaliza = (s) =>
    (s ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const parseDateSafe = (value) => {
    if (!value) return null;
    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const hasFirmaWM = (p) => Boolean(p?.datos?._firmas_v1?.responsable_wemotion);
  const hasFirmaCliente = (p) => Boolean(p?.datos?._firmas_v1?.responsable_cliente);

  const enviadoA = (p) =>
    (p?.datos?._pdf_v1?.enviado_a || p?.datos?._meta_v1?.enviado_a || p?.datos?.email || "")
      .toString()
      .trim();

  // --------------------------
  // Map usuarios (id -> nombre)
  // --------------------------
  const mapUsuarios = useMemo(() => {
    const m = {};
    (usuarios || []).forEach((u) => {
      const full = [u.nombre, u.apellidos].filter(Boolean).join(" ").trim();
      m[String(u.id)] = full || u.nombre || `Usuario ${u.id}`;
    });
    return m;
  }, [usuarios]);

  // --------------------------
  // Carga
  // --------------------------
  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [resP, resUsers] = await Promise.all([
        fetch(`${Global.url}partes-trabajo/manual`, { headers }),
        fetch(`${Global.url}usuarios`, { headers }), // ✅ para columna Creador
      ]);

      if (!resP.ok) {
        const t = await resP.text().catch(() => "");
        throw new Error(t || "No se pudieron cargar los partes manuales.");
      }

      const jsonP = await resP.json().catch(() => ({}));
      const lista = Array.isArray(jsonP) ? jsonP : Array.isArray(jsonP?.data) ? jsonP.data : [];
      setPartes(lista);

      if (resUsers.ok) {
        const dataUsers = await resUsers.json().catch(() => []);
        setUsuarios(Array.isArray(dataUsers) ? dataUsers : []);
      } else {
        // no rompemos si falla
        setUsuarios([]);
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Error cargando partes manuales.");
    } finally {
      setCargando(false);
    }
  }, [headers, toast]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // --------------------------
  // Modal email
  // --------------------------
  const openSendModal = useCallback(
    (row) => {
      setSelectedParte(row || null);
      const prefill = (enviadoA(row) || row?.datos?.cliente?.email || "").toString().trim();
      setEmailTo(prefill);
      setModalOpen(true);
    },
    []
  );

  const closeSendModal = () => {
    if (sending) return;
    setModalOpen(false);
    setSelectedParte(null);
    setEmailTo("");
  };

  const enviarParteEmail = async () => {
    const parteId = selectedParte?.id;
    const email = String(emailTo || "").trim();

    if (!parteId) return toast.error("No se pudo determinar el parte.");
    if (!isEmailValid(email)) return toast.error("Email inválido.");

    setSending(true);
    try {
      const res = await fetch(`${Global.url}partes-trabajo/manual/${parteId}/enviar-email`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        let msg = txt || "No se pudo enviar el parte.";
        try {
          const j = JSON.parse(txt);
          msg = j?.message || j?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      toast.success("Parte enviado por email.");
      closeSendModal();
      await cargar().catch(() => {});
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Error enviando el parte.");
    } finally {
      setSending(false);
    }
  };

  // --------------------------
  // Filtrado + ordenación
  // --------------------------
  const q = normaliza(busquedaTexto);

  const partesFiltrados = useMemo(() => {
    return (partes || []).filter((p) => {
      const fecha = parseDateSafe(p.creado_en);
      if (!fecha) return false;

      const anio = String(fecha.getFullYear());
      const mes = String(fecha.getMonth() + 1).padStart(2, "0");

      if (filtroAnio && anio !== filtroAnio) return false;
      if (filtroMes && mes !== filtroMes) return false;

      if (fechaDesde && fecha < new Date(fechaDesde)) return false;
      if (fechaHasta) {
        const hastaExclusive = new Date(new Date(fechaHasta).setDate(new Date(fechaHasta).getDate() + 1));
        if (fecha >= hastaExclusive) return false;
      }

      const titulo = (p.titulo || p.datos?.titulo || "").toString();
      const email = enviadoA(p);
      const creador = mapUsuarios[String(p.creado_por)] || (p.creado_por ? `Usuario ${p.creado_por}` : "");

      const coincideTexto =
        !q ||
        normaliza(titulo).includes(q) ||
        normaliza(String(p.id || "")).includes(q) ||
        normaliza(email).includes(q) ||
        normaliza(creador).includes(q);

      return coincideTexto;
    });
  }, [partes, filtroAnio, filtroMes, fechaDesde, fechaHasta, q, mapUsuarios]);

  useEffect(() => {
    setPage(0);
  }, [filtroAnio, filtroMes, fechaDesde, fechaHasta, busquedaTexto]);

  const partesOrdenados = useMemo(() => {
    const arr = [...partesFiltrados];

    arr.sort((a, b) => {
      let va = a[orden.campo];
      let vb = b[orden.campo];

      if (orden.campo === "titulo") {
        va = (a.titulo || a.datos?.titulo || "").toString();
        vb = (b.titulo || b.datos?.titulo || "").toString();
      }

      if (orden.campo === "creado_por") {
        va = mapUsuarios[String(a.creado_por)] || (a.creado_por ? `Usuario ${a.creado_por}` : "");
        vb = mapUsuarios[String(b.creado_por)] || (b.creado_por ? `Usuario ${b.creado_por}` : "");
      }

      if (orden.campo === "creado_en" || orden.campo === "actualizado_en") {
        va = parseDateSafe(va)?.getTime() ?? 0;
        vb = parseDateSafe(vb)?.getTime() ?? 0;
      }

      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();

      va = va ?? "";
      vb = vb ?? "";

      if (va < vb) return orden.ascendente ? -1 : 1;
      if (va > vb) return orden.ascendente ? 1 : -1;
      return 0;
    });

    return arr;
  }, [partesFiltrados, orden, mapUsuarios]);

  // --------------------------
  // Rows/Cols
  // --------------------------
  const rows = useMemo(() => {
    const myId = auth?.id ? String(auth.id) : null;

    return partesOrdenados.map((p) => {
      const titulo = (p.titulo || p.datos?.titulo || "").toString().trim();
      const isOwner = myId ? String(p.creado_por ?? "") === myId : false;
      const email = enviadoA(p);

      const creadorTxt = mapUsuarios[String(p.creado_por)] || (p.creado_por ? `Usuario ${p.creado_por}` : "-");

      return {
        ...p,
        id: p.id,
        isOwner,
        titulo_txt: titulo || `Parte ${String(p.id || "").slice(0, 8)}`,
        creador_txt: creadorTxt,
        creado_en_txt: fmtDate(p.creado_en),
        actualizado_en_txt: fmtDate(p.actualizado_en),
        firma_wm: hasFirmaWM(p),
        firma_cliente: hasFirmaCliente(p),
        enviado_a_txt: email || "-",
      };
    });
  }, [partesOrdenados, auth?.id, mapUsuarios]);

  const columns = useMemo(() => {
    return [
      {
        field: "titulo_txt",
        headerName: "Título",
        flex: 1,
        minWidth: 280,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      { field: "creador_txt", headerName: "Creador", minWidth: 220, sortable: false },
      {
        field: "firmas",
        headerName: "Firmas",
        minWidth: 200,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={params.row.firma_wm ? "WM ✓" : "WM —"} variant={params.row.firma_wm ? "filled" : "outlined"} />
            <Chip size="small" label={params.row.firma_cliente ? "Cliente ✓" : "Cliente —"} variant={params.row.firma_cliente ? "filled" : "outlined"} />
          </Stack>
        ),
      },
      {
        field: "enviado_a_txt",
        headerName: "Enviado a",
        minWidth: 220,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2" noWrap sx={{ opacity: params.value && params.value !== "-" ? 0.9 : 0.55 }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      { field: "creado_en_txt", headerName: "Creado", minWidth: 170, sortable: false },
      { field: "actualizado_en_txt", headerName: "Actualizado", minWidth: 170, sortable: false },
      {
        field: "abrir",
        headerName: "Parte",
        minWidth: 150,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() =>
              navigate(`/app/misPartesManuales/${params.row.id}`, {
                state: { readOnly: !params.row.isOwner },
              })
            }
          >
            Abrir
          </Button>
        ),
      },
      {
        field: "enviar",
        headerName: "Enviar",
        minWidth: 170,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Tooltip title={params.row.isOwner ? "Enviar por email" : "Solo el creador puede enviar"}>
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => openSendModal(params.row)}
                disabled={!params.row.isOwner}
              >
                Enviar
              </Button>
            </span>
          </Tooltip>
        ),
      },
    ];
  }, [navigate, openSendModal]);

  const onHeaderClick = (field) => {
    const map = {
      titulo_txt: "titulo",
      creador_txt: "creado_por",
      creado_en_txt: "creado_en",
      actualizado_en_txt: "actualizado_en",
    };

    const campoOrden = map[field] || null;
    if (!campoOrden) return;

    setOrden((prev) => ({
      campo: campoOrden,
      ascendente: prev.campo === campoOrden ? !prev.ascendente : true,
    }));
  };

  const limpiarFiltros = () => {
    setFiltroAnio("");
    setFiltroMes("");
    setFechaDesde("");
    setFechaHasta("");
    setBusquedaTexto("");
    toast.info("Filtros limpiados.");
  };

  // --------------------------
  // Render
  // --------------------------
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Partes Manuales
        </Typography>


        <Alert severity="info">
          Este listado es <strong>solo lectura</strong>. Para crear un parte manual nuevo, ve a <strong>“Mis partes”</strong>.
        </Alert>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              alignItems: "end",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(12, 1fr)",
              },
            }}
          >
            <TextField
              size="small"
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "span 1", md: "span 2" } }}
            />

            <TextField
              size="small"
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "span 1", md: "span 2" } }}
            />

            <FormControl size="small" fullWidth sx={{ gridColumn: { xs: "1 / -1", sm: "span 1", md: "span 2" } }}>
              <InputLabel>Año</InputLabel>
              <Select label="Año" value={filtroAnio} onChange={(e) => setFiltroAnio(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 16 }, (_, i) => {
                  const year = 2025 + i;
                  return (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth sx={{ gridColumn: { xs: "1 / -1", sm: "span 1", md: "span 2" } }}>
              <InputLabel>Mes</InputLabel>
              <Select label="Mes" value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={String(i + 1).padStart(2, "0")}>
                    {new Date(0, i).toLocaleString("es-ES", { month: "long" })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Buscar"
              value={busquedaTexto}
              onChange={(e) => setBusquedaTexto(e.target.value)}
              placeholder="Título, id, email, creador…"
              fullWidth
              sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "span 6" } }}
            />

            <Stack
              direction="row"
              spacing={1}
              sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "span 2" }, justifySelf: { md: "end" } }}
            >
              <Button variant="outlined" startIcon={<ClearIcon />} onClick={limpiarFiltros} fullWidth>
                Limpiar
              </Button>
            </Stack>
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 620, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={cargando}
            disableRowSelectionOnClick
            pagination
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            onColumnHeaderClick={(params) => onHeaderClick(params.field)}
            sx={{
              "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
              "& .MuiDataGrid-cellContent": { width: "100%" },
              "& .MuiDataGrid-columnHeaders": { borderBottom: "1px solid rgba(0,0,0,0.08)" },
              "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(0,0,0,0.06)" },
            }}
          />
        </Paper>
      </Stack>

      {/* Modal envío email */}
      <Dialog open={modalOpen} onClose={closeSendModal} fullWidth maxWidth="sm">
        <DialogTitle>Enviar parte manual por email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">Se generará un PDF con el parte y se enviará como adjunto.</Alert>

            <TextField
              label="Email destino"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              fullWidth
              autoFocus
              placeholder="cliente@empresa.com"
              error={Boolean(emailTo) && !isEmailValid(emailTo)}
              helperText={emailTo && !isEmailValid(emailTo) ? "Introduce un email válido." : " "}
            />

            {selectedParte ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Parte: {selectedParte?.titulo || selectedParte?.datos?.titulo || String(selectedParte?.id || "").slice(0, 8)} ·
                Creado: {fmtDate(selectedParte?.creado_en)}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSendModal} disabled={sending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={enviarParteEmail}
            disabled={sending || !isEmailValid(emailTo) || !selectedParte?.id}
          >
            {sending ? "Enviando…" : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListadoPartesManuales;